import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  ParentOrgRequest,
  ParentOrgResponse,
  ParentOrgSummaryResponse,
} from '../../../@types/parentOrganization/parentOrganization.type';
import axiosInstance from '../../../utils/axios';

// ---------- Types ----------
interface RequestState<T = any> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface ParentOrgState {
  list: RequestState<ParentOrgResponse[]>;
  summary: RequestState<ParentOrgSummaryResponse>;
  single: RequestState<ParentOrgResponse>;
  create: RequestState<ParentOrgResponse>;
  update: RequestState<ParentOrgResponse>;
  delete: RequestState<string>;
}

// ---------- Initial State ----------
const initialRequestState = <T,>(): RequestState<T> => ({
  data: null,
  loading: false,
  error: null,
});

const initialState: ParentOrgState = {
  list: initialRequestState<ParentOrgResponse[]>(),
  summary: initialRequestState<ParentOrgSummaryResponse>(),
  single: initialRequestState<ParentOrgResponse>(),
  create: initialRequestState<ParentOrgResponse>(),
  update: initialRequestState<ParentOrgResponse>(),
  delete: initialRequestState<string>(),
};

// ---------- Thunks ----------
const BASE_URL = `${process.env.REACT_APP_HOST_API_KEY}/api/v1/organizations`;

export const fetchParentOrgs = createAsyncThunk(
  'parentOrgs/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      // TODO: for that moment i am not configuring year pagination on react so i am fetching 1000 ele from back, must be handled later,
      const res = await axiosInstance.get(`${process.env.REACT_APP_HOST_API_KEY}/api/v1/organizations/parents/?page=0&size=1000`);
      return res.data.content as ParentOrgResponse[];
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Fetch failed');
    }
  }
);

export const fetchParentOrgsSummary = createAsyncThunk(
  'parentOrgs/fetchSummary',
  async (
    params: { page?: number; size?: number; q?: string; status?: string; days?: number } | undefined,
    { rejectWithValue }
  ) => {
    try {
      const { page = 0, size = 1000, q, status, days = 30 } = params || {};
      const res = await axiosInstance.get(
        `${process.env.REACT_APP_HOST_API_KEY}/api/v1/organizations/parents/summary`,
        {
          params: { page, size, q, status, days },
        }
      );
      return res.data as ParentOrgSummaryResponse;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Summary fetch failed');
    }
  }
);

export const fetchParentOrgById = createAsyncThunk(
  'parentOrgs/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`${BASE_URL}/${id}`);
      return res.data as ParentOrgResponse;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Fetch failed');
    }
  }
);

export const createParentOrg = createAsyncThunk<
  ParentOrgResponse,
  FormData,
  { rejectValue: string }
>(
  'parentOrgs/create',
  async (formData, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(BASE_URL, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return res.data as ParentOrgResponse;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Create failed');
    }
  }
);


export const updateParentOrg = createAsyncThunk<
  ParentOrgResponse,
  { id: string; data: FormData },
  { rejectValue: string }
>(
  'parentOrgs/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(`${BASE_URL}/${id}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return res.data as ParentOrgResponse;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Update failed');
    }
  }
);


export const deleteParentOrg = createAsyncThunk(
  'parentOrgs/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/${id}`);
      console.log('delete handled successfully')
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Delete failed');
    }
  }
);

// ---------- Slice ----------
const parentOrgSlice = createSlice({
  name: 'parentOrgs',
  initialState,
  reducers: {
    clearCreateState: (state) => {
      state.create = initialRequestState();
    },
    clearUpdateState: (state) => {
      state.update = initialRequestState();
    },
    clearDeleteState: (state) => {
      state.delete = initialRequestState();
    },
  },
  extraReducers: (builder) => {
    // Fetch all
    builder.addCase(fetchParentOrgs.pending, (state) => {
      state.list.loading = true;
      state.list.error = null;
    });
    builder.addCase(fetchParentOrgs.fulfilled, (state, action) => {
      state.list.loading = false;
      state.list.data = action.payload;
    });
    builder.addCase(fetchParentOrgs.rejected, (state, action) => {
      state.list.loading = false;
      state.list.error = action.payload as string;
    });

    // Fetch summary
    builder.addCase(fetchParentOrgsSummary.pending, (state) => {
      state.summary.loading = true;
      state.summary.error = null;
    });
    builder.addCase(fetchParentOrgsSummary.fulfilled, (state, action) => {
      state.summary.loading = false;
      state.summary.data = action.payload;
    });
    builder.addCase(fetchParentOrgsSummary.rejected, (state, action) => {
      state.summary.loading = false;
      state.summary.error = action.payload as string;
    });

    // Fetch by ID
    builder.addCase(fetchParentOrgById.pending, (state) => {
      state.single.loading = true;
      state.single.error = null;
    });
    builder.addCase(fetchParentOrgById.fulfilled, (state, action) => {
      state.single.loading = false;
      state.single.data = action.payload;
    });
    builder.addCase(fetchParentOrgById.rejected, (state, action) => {
      state.single.loading = false;
      state.single.error = action.payload as string;
    });

    // Create
    builder.addCase(createParentOrg.pending, (state) => {
      state.create.loading = true;
      state.create.error = null;
    });
    builder.addCase(createParentOrg.fulfilled, (state, action) => {
      state.create.loading = false;
      state.create.data = action.payload;
    });
    builder.addCase(createParentOrg.rejected, (state, action) => {
      state.create.loading = false;
      state.create.error = action.payload as string;
    });

    // Update
    builder.addCase(updateParentOrg.pending, (state) => {
      state.update.loading = true;
      state.update.error = null;
    });
    builder.addCase(updateParentOrg.fulfilled, (state, action) => {
      state.update.loading = false;
      state.update.data = action.payload;
    });
    builder.addCase(updateParentOrg.rejected, (state, action) => {
      state.update.loading = false;
      state.update.error = action.payload as string;
    });

    // Delete
    builder.addCase(deleteParentOrg.pending, (state) => {
      state.delete.loading = true;
      state.delete.error = null;
    });
    builder.addCase(deleteParentOrg.fulfilled, (state, action) => {
      state.delete.loading = false;
      state.delete.data = action.payload;
    });
    builder.addCase(deleteParentOrg.rejected, (state, action) => {
      state.delete.loading = false;
      state.delete.error = action.payload as string;
    });
  },
});

export const { clearCreateState, clearUpdateState, clearDeleteState } = parentOrgSlice.actions;
export default parentOrgSlice.reducer;
